<template>
  <section>
    <RenderlessCalendar
      v-slot="slotProps"
      :max-date="maxDate"
      :min-date="minDate"
      :custom-number-of-months="numberOfMonth"
      :default-selected-dates="selectedDates"
      :locale="
        i18next.language === AppLocale.KK
          ? datePickerLocales.KK
          : datePickerLocales.RU
      "
      mode="range"
      view-mode="custom"
      @onDateChange="onDatesSelect"
    >
      <div class="flex w-full items-center space-x-3 overflow-hidden px-3">
        <button
          type="button"
          :disabled="slideNumber === 0"
          class="flex h-10 w-10 items-center justify-center rounded-xl duration-200 hover:bg-gray-75 active:scale-90 disabled:text-gray-300"
          @click="emit('onPrevSlide')"
        >
          <!--prettier-ignore-->
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" fill="none" class="stoke-current"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8.334.833 1.667 7.5l6.667 6.666"/></svg>
        </button>
        <div class="flex-1 overflow-hidden">
          <div
            class="flex duration-500"
            :style="`transform: translateX(-${slideNumber * 100}%)`"
          >
            <div
              v-for="(calendar, i) in chunkCalendar(slotProps.calendar)"
              :key="`_calendar-${i}`"
              class="grid min-w-full grid-cols-2 gap-6"
            >
              <div v-for="view in calendar" :key="`${view.year}-${view.month}`">
                <div>
                  <strong class="block text-center capitalize">
                    {{ slotProps.monthNames[view.month].full }}
                    <span v-if="view.year !== slotProps.currentYear">
                      {{ view.year }}
                    </span>
                  </strong>
                  <ul class="mt-3 grid grid-cols-7 justify-items-center">
                    <li
                      v-for="weekday in slotProps.weekDayNames"
                      :key="weekday.short"
                      class="text-sm"
                    >
                      {{ weekday.short }}
                    </li>
                  </ul>
                  <ul class="mt-3 grid grid-cols-7">
                    <li
                      v-for="date in view.dates"
                      :key="date.ms"
                      class="text-center"
                    >
                      <button
                        v-if="!date.isOtherMonthDay"
                        type="button"
                        class="h-12 w-full font-semibold duration-200 hover:bg-gray-150 hover:text-gray-700 disabled:text-gray-400"
                        :disabled="
                          slotProps.getModifiers(date).isDisabled ||
                          !isDateAvailable(date.formatted)
                        "
                        :class="{
                          'hover:rounded-xl':
                            !slotProps.getModifiers(date).isSelected &&
                            !slotProps.getModifiers(date).isBetween,
                          'rounded-xl':
                            slotProps.getModifiers(date).isSelected &&
                            slotProps.selectedDates.length === 1,
                          'rounded-l-xl':
                            slotProps.getModifiers(date).isFirst &&
                            slotProps.getModifiers(date).isSelected,
                          'rounded-r-xl':
                            slotProps.getModifiers(date).isLast &&
                            slotProps.getModifiers(date).isSelected,
                          'bg-purple-400  text-white hover:bg-purple-500 hover:text-white disabled:text-white':
                            slotProps.getModifiers(date).isSelected,
                          'bg-gray-150': slotProps.getModifiers(date).isBetween,
                        }"
                        @click="slotProps.onDateSelect(date)"
                      >
                        {{ date.day }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          :disabled="slideNumber === maxSlideCount"
          class="flex h-10 w-10 items-center justify-center rounded-xl hover:bg-gray-75 active:scale-90 disabled:text-gray-300"
          @click="emit('onNextSlide')"
        >
          <!--prettier-ignore-->
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" fill="none" class="stroke-current"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1.667 14.167 8.332 7.5 1.666.833"/></svg>
        </button>
      </div>
    </RenderlessCalendar>
  </section>
</template>

<script lang="ts" setup>
  import i18next from 'i18next';
  import { chunk } from 'lodash-es';
  import { RenderlessCalendar } from 'vue-renderless-calendar';
  import type { Calendar, CalendarDate } from 'vue-renderless-calendar';

  import { AppLocale } from '@shared/i18n';
  import type { DateLib } from '@shared/lib';
  import { DEPARTURE_FIELD, datePickerLocales } from '../config';
  import type { DateField } from '../config';

  const props = defineProps<{
    slideNumber: number;
    maxSlideCount: number;
    numberOfMonth: number;
    selectedDates: DateLib.DateProp[];
    availableDepartureDates?: DateLib.DateProp[];
    availableArrivalDates?: DateLib.DateProp[];
    maxDate?: DateLib.DateProp;
    minDate: DateLib.DateProp;
    selectedDateField: DateField;
  }>();

  const emit = defineEmits<{
    (e: 'onDateChange', value: string[]): void;
    (e: 'onNextSlide'): void;
    (e: 'onPrevSlide'): void;
  }>();

  function chunkCalendar(calendarArray: Array<Calendar>) {
    return chunk(calendarArray, 2);
  }

  function isDateAvailable(date: DateLib.DateProp) {
    if (!props.availableDepartureDates && !props.availableArrivalDates) {
      return true;
    }
    if (props.selectedDateField === DEPARTURE_FIELD) {
      return props.availableDepartureDates?.includes(date);
    }
    return props.availableArrivalDates?.includes(date);
  }

  const onDatesSelect = (dates: CalendarDate[]) => {
    const formattedDates = dates.map(date => date.formatted);
    emit('onDateChange', formattedDates);
  };
</script>
